import useI18n from '@/shared/hooks/useI18n'
import { BodyTd, FooterTd, HeaderTh, TableBody, TableBodyRow } from './typography'

interface ElectricitySource {
  type: string
  totalConsumptionShare: number
  weightedAvgEmissionFactor: number
}

interface AggregatedElectricity {
  sources: ElectricitySource[]
}

export default function FinalizedReportElectricityEmissionsTable({
  aggregatedElectricity,
  electricityCombined,
  columnNames,
  combinedLabel,
  selectedPdfLanguage
}: {
  aggregatedElectricity: AggregatedElectricity
  electricityCombined: {
    totalConsumptionShare: number
    weightedAvgEmissionFactor: number
  }
  columnNames?: {
    electricitySource: string
    consumptionShare: string
    emissionFactor: string
  }
  combinedLabel?: string
  selectedPdfLanguage?: string
}) {
  const { t, numberFormatter3DecimalsGB } = useI18n()
  const hasMoreThanOneSource = aggregatedElectricity.sources.length > 1

  const tLng = { lng: selectedPdfLanguage }
  const localColumnNames = columnNames ?? {
    electricitySource: t(
      'finalizedReportDocument:emissions.indirect.table.electricitySource',
      tLng
    ),
    consumptionShare: t('finalizedReportDocument:emissions.indirect.table.consumptionShare', tLng),
    emissionFactor: t('finalizedReportDocument:emissions.indirect.table.emissionFactor', tLng)
  }
  const localCombinedLabel =
    combinedLabel ?? t('finalizedReportDocument:emissions.indirect.table.combinedRow', tLng)

  return (
    <table className="w-full">
      <thead>
        <tr>
          <HeaderTh>{localColumnNames.electricitySource}</HeaderTh>
          <HeaderTh isNumeric>{localColumnNames.consumptionShare}</HeaderTh>
          <HeaderTh isNumeric>{localColumnNames.emissionFactor}</HeaderTh>
        </tr>
      </thead>
      <TableBody>
        {aggregatedElectricity.sources.map(source => (
          <TableBodyRow key={source.type}>
            <BodyTd>{t(`common:electricitySources.${source.type}.title`, tLng)}</BodyTd>
            <BodyTd isNumeric>
              {numberFormatter3DecimalsGB(source.totalConsumptionShare * 100)}%
            </BodyTd>
            <BodyTd isNumeric>
              {numberFormatter3DecimalsGB(source.weightedAvgEmissionFactor)} tCO2/MWh
            </BodyTd>
          </TableBodyRow>
        ))}
      </TableBody>
      {hasMoreThanOneSource && (
        <tfoot className="font-bold">
          <tr>
            <FooterTd>{localCombinedLabel}</FooterTd>
            <FooterTd isNumeric>
              {numberFormatter3DecimalsGB(electricityCombined.totalConsumptionShare * 100)}%
            </FooterTd>
            <FooterTd isNumeric>
              {numberFormatter3DecimalsGB(electricityCombined.weightedAvgEmissionFactor)} tCO2/MWh
            </FooterTd>
          </tr>
        </tfoot>
      )}
    </table>
  )
}
